import { t } from "../../i18n";
import { lazy, Suspense } from "react";
import type { User } from "@lumen/shared";
import {
  BarChart3,
  Bot,
  BookOpen,
  Boxes,
  Building2,
  Archive,
  Cloud,
  Container,
  CreditCard,
  FileTerminal,
  GitBranch,
  HardDrive,
  KeyRound,
  ServerCog,
  ShieldCheck,
  Settings2,
  TerminalSquare,
  Upload,
  UserRound,
  Users,
  Wrench,
  Workflow,
} from "lucide-react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ModelsPage } from "../models/ModelsPage";
import { CustomModelsPage } from "../models/CustomModelsPage";
import { ProfilePage } from "./ProfilePage";
import { ApiKeysPage } from "./ApiKeysPage";
import { ProvidersPage } from "../integrations/ProvidersPage";
import { UsersPage } from "../organization/UsersPage";
import { McpPage, SkillsPage } from "../integrations/ExtensionsPage";
import { DataImportPage } from "../DataImportPage";
import { AgentWorkspacesPage } from "../agent-workspaces/AgentWorkspacesPage";
import { AgentsPage } from "../agent-workspaces/AgentsPage";
import { UsagePage } from "../usage/UsagePage";
import { RepositoriesPage } from "../organization/RepositoriesPage";
import { ServerLogsPage } from "../ServerLogsPage";
import { ServerLogExplorerPage } from "../ServerLogExplorerPage";
import { GeneralSettingsPage } from "./GeneralSettingsPage";
import { WorkflowSettingsPage } from "../automations/WorkflowSettingsPage";
import { AutomationsPage } from "../automations/AutomationsPage";
import { AutomationRunPage } from "../automations/AutomationRunPage";
import { NetworkPolicyPage } from "../organization/NetworkPolicyPage";
import { BillingPage } from "../usage/BillingPage";
import { IntegrationsPage } from "../integrations/IntegrationsPage";
import { OrganizationSwitcher } from "../../components/organization/OrganizationSwitcher";
import { WorkspaceVolumesPage } from "../agent-workspaces/WorkspaceVolumesPage";
import { WorkspaceStoragePage } from "../agent-workspaces/WorkspaceStoragePage";
import { PersonalizationPage } from "./PersonalizationPage";
import { ArtifactsPage } from "../artifacts/ArtifactsPage";
import {
  OrganizationCreatePage,
  OrganizationEditPage,
  OrganizationPage,
} from "../organization/OrganizationPage";
import {
  SiteOrganizationCreatePage,
  SiteOrganizationEditorPage,
  SiteOrganizationsPage,
} from "../site-organizations/SiteOrganizationsPage";

const ToolsPage = lazy(() =>
  import("../ToolsPage").then((module) => ({ default: module.ToolsPage })),
);
const CustomProvidersPage = lazy(() =>
  import("../integrations/CustomProvidersPage").then((module) => ({
    default: module.CustomProvidersPage,
  })),
);

type Item = {
  to: string;
  label: string;
  icon: typeof UserRound;
  visible: boolean;
};

export function SettingsArea({
  user,
  onUserChange,
  onConversationsImported,
}: {
  user: User;
  onUserChange: (user: User) => void;
  onConversationsImported: () => void | Promise<void>;
}) {
  const location = useLocation();
  const siteOrganizationId = new URLSearchParams(location.search).get(
    "organizationId",
  );
  const organizationPermissions = user.organizationPermissions ?? [];
  const sitePermissions = user.sitePermissions ?? [];
  const orgHas = (permission: string) =>
    organizationPermissions.includes(permission as never);
  const siteHas = (permission: string) =>
    sitePermissions.includes(permission as never);
  const items: Item[] = [
    {
      to: "/settings/api-keys",
      label: "API keys",
      icon: KeyRound,
      visible:
        orgHas("chat:use") && orgHas("models:read") && !user.supportAccess,
    },
    {
      to: "/settings/profile",
      label: "Profile",
      icon: UserRound,
      visible: true,
    },
    {
      to: "/settings/personalization",
      label: "Personalization",
      icon: Settings2,
      visible: orgHas("chat:use"),
    },
    {
      to: "/settings/automations",
      label: "Automations",
      icon: Workflow,
      visible: orgHas("automations:use"),
    },
    {
      to: "/settings/vault",
      label: "Artifact vault",
      icon: Archive,
      visible: orgHas("chat:use"),
    },
    {
      to: "/settings/import",
      label: "Data",
      icon: Upload,
      visible: orgHas("chat:use"),
    },
    {
      to: "/settings/agents",
      label: "Agents",
      icon: Bot,
      visible:
        orgHas("chat:use") ||
        Boolean(
          siteOrganizationId && siteHas("site:organization-access:manage"),
        ),
    },
    {
      to: "/settings/skills",
      label: "Skills",
      icon: BookOpen,
      visible:
        orgHas("chat:use") ||
        Boolean(
          siteOrganizationId && siteHas("site:organization-access:manage"),
        ),
    },
    {
      to: "/settings/mcp",
      label: "MCP servers",
      icon: ServerCog,
      visible: orgHas("chat:use") || siteHas("site:mcp:manage"),
    },
    {
      to: "/settings/integrations",
      label: "Integrations",
      icon: Boxes,
      visible:
        orgHas("integrations:use") ||
        Boolean(
          siteOrganizationId && siteHas("site:organization-access:manage"),
        ),
    },
    {
      to: "/settings/agent-workspaces",
      label: "Agent workspaces",
      icon: TerminalSquare,
      visible:
        orgHas("agent-workspaces:use") ||
        siteHas("site:agent-workspaces:manage") ||
        Boolean(
          siteOrganizationId && siteHas("site:organization-access:manage"),
        ),
    },
    {
      to: "/settings/repositories",
      label: "Repositories",
      icon: GitBranch,
      visible: orgHas("agent-workspaces:use"),
    },
    {
      to: "/settings/workspace-storage",
      label: t("Workspace storage"),
      icon: HardDrive,
      visible:
        orgHas("organization:manage") ||
        siteHas("site:agent-workspaces:manage"),
    },
    {
      to: "/settings/providers",
      label: t("Providers"),
      icon: Cloud,
      visible:
        siteHas("site:providers:read") ||
        orgHas("providers:personal") ||
        (user.capabilities.organizationProviders && orgHas("models:read")),
    },
    {
      to: "/settings/custom-providers",
      label: t("Custom providers"),
      icon: Container,
      visible:
        siteHas("site:providers:read") ||
        siteHas("site:providers:write") ||
        orgHas("providers:write") ||
        orgHas("organization:manage") ||
        orgHas("providers:personal"),
    },
    {
      to: "/settings/organization",
      label: "Organization",
      icon: Building2,
      visible: orgHas("organization:manage"),
    },
    {
      to: "/settings/billing",
      label: "Billing",
      icon: CreditCard,
      visible: orgHas("billing:read"),
    },
    {
      to: "/settings/organizations",
      label: "Organizations",
      icon: Building2,
      visible: siteHas("site:organizations:read"),
    },
    {
      to: "/settings/general",
      label: t("General"),
      icon: Settings2,
      visible: siteHas("site:users:manage"),
    },
    {
      to: "/settings/network-policy",
      label: t("Network policy"),
      icon: ShieldCheck,
      visible: siteHas("site:settings:manage"),
    },
    {
      to: "/settings/models",
      label: "Models",
      icon: Boxes,
      visible: orgHas("models:read") || siteHas("site:models:read"),
    },
    {
      to: "/settings/workflow",
      label: t("Workflow"),
      icon: Workflow,
      visible: orgHas("models:read") || siteHas("site:models:write"),
    },
    {
      to: "/settings/tools",
      label: "Tools",
      icon: Wrench,
      visible: siteHas("site:tools:write"),
    },
    {
      to: "/settings/usage",
      label: "Usage",
      icon: BarChart3,
      visible: orgHas("usage:read"),
    },
    {
      to: "/settings/logs",
      label: "Server logs",
      icon: FileTerminal,
      visible: siteHas("site:logs:read"),
    },
    {
      to: "/settings/people",
      label: "Site people & permissions",
      icon: Users,
      visible: siteHas("site:users:manage"),
    },
  ];
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
      <aside className="flex min-h-0 shrink-0 flex-col border-b border-stone-200 bg-white/60 p-3 md:h-full md:w-72 md:border-r md:border-b-0 md:p-5 dark:border-white/10 dark:bg-white/2">
        <div className="mb-4 hidden px-2 md:block">
          <p className="text-xs font-bold tracking-[.18em] text-lumen-600 uppercase">
            {t("Workspace")}
          </p>
          <h1 className="mt-1 text-xl font-bold">{t("Settings")}</h1>
        </div>
        <OrganizationSwitcher />
        <nav className="flex max-h-32 min-h-0 gap-1 overflow-x-auto md:max-h-none md:flex-1 md:flex-col md:overflow-x-hidden md:overflow-y-auto">
          {items
            .filter((item) => item.visible)
            .map(({ to, label, icon: Icon }) => (
              <div key={to} className="contents md:block">
                {to === "/settings/organization" && (
                  <div className="mx-2 hidden border-t border-stone-200 pt-4 text-[10px] font-bold tracking-[.16em] text-stone-400 uppercase md:mt-3 md:block dark:border-white/10">
                    {t("Organization administration")}
                  </div>
                )}
                {to === "/settings/organizations" && (
                  <div className="mx-2 hidden border-t border-stone-200 pt-4 text-[10px] font-bold tracking-[.16em] text-stone-400 uppercase md:mt-3 md:block dark:border-white/10">
                    {t("Site administration")}
                  </div>
                )}
                <Link
                  to={to}
                  className={`button shrink-0 justify-start whitespace-nowrap md:w-full md:whitespace-normal ${location.pathname === to || location.pathname.startsWith(`${to}/`) ? "bg-lumen-50 text-lumen-800 dark:bg-lumen-900/30 dark:text-lumen-200" : "text-stone-600 dark:text-stone-300"}`}
                >
                  <Icon className="shrink-0" size={17} />
                  <span className="min-w-0 text-left leading-5">{label}</span>
                </Link>
              </div>
            ))}
        </nav>
      </aside>
      <Routes>
        <Route
          path="api-keys"
          element={
            !user.supportAccess ? (
              <ApiKeysPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="vault"
          element={
            orgHas("chat:use") ? (
              <ArtifactsPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="personalization"
          element={
            orgHas("chat:use") ? (
              <PersonalizationPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="automations"
          element={
            orgHas("automations:use") ? (
              <AutomationsPage user={user} />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="automations/runs/:id"
          element={
            orgHas("automations:use") ? (
              <AutomationRunPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="profile"
          element={<ProfilePage user={user} onUserChange={onUserChange} />}
        />
        <Route
          path="import"
          element={
            orgHas("chat:use") ? (
              <DataImportPage onImported={onConversationsImported} />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="agents"
          element={
            orgHas("chat:use") ||
            (siteOrganizationId &&
              siteHas("site:organization-access:manage")) ? (
              <AgentsPage
                canManageOrganization={
                  orgHas("skills:write") || Boolean(siteOrganizationId)
                }
                canManagePlatform={siteHas("site:agents:manage")}
                organizationId={siteOrganizationId ?? undefined}
                canManageSiteOrganization={Boolean(siteOrganizationId)}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="skills"
          element={
            orgHas("chat:use") ||
            (siteOrganizationId &&
              siteHas("site:organization-access:manage")) ? (
              <SkillsPage
                canManageOrganization={
                  orgHas("skills:write") || Boolean(siteOrganizationId)
                }
                canManagePlatform={siteHas("site:skills:manage")}
                organizationId={siteOrganizationId ?? undefined}
                canManageSiteOrganization={Boolean(siteOrganizationId)}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="mcp"
          element={
            orgHas("chat:use") || siteHas("site:mcp:manage") ? (
              <McpPage
                canManage={orgHas("mcp:manage") || siteHas("site:mcp:manage")}
                canManagePlatform={
                  siteHas("site:mcp:manage") && !orgHas("mcp:manage")
                }
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="integrations"
          element={
            orgHas("integrations:use") ||
            (siteOrganizationId &&
              siteHas("site:organization-access:manage")) ? (
              <IntegrationsPage
                user={user}
                organizationId={siteOrganizationId ?? undefined}
                siteOrganizationManagement={Boolean(siteOrganizationId)}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="agent-workspaces"
          element={
            orgHas("agent-workspaces:use") ||
            siteHas("site:agent-workspaces:manage") ||
            (siteOrganizationId &&
              siteHas("site:organization-access:manage")) ? (
              <AgentWorkspacesPage
                canManage={
                  orgHas("organization:manage") ||
                  siteHas("site:agent-workspaces:manage")
                }
                canManagePlatform={siteHas("site:agent-workspaces:manage")}
                canManageOrganization={
                  orgHas("organization:manage") ||
                  Boolean(
                    siteOrganizationId &&
                    siteHas("site:organization-access:manage"),
                  )
                }
                organizationId={siteOrganizationId ?? undefined}
                siteOrganizationManagement={Boolean(siteOrganizationId)}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="repositories"
          element={
            orgHas("agent-workspaces:use") ? (
              <RepositoriesPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="workspace-volumes"
          element={
            orgHas("agent-workspaces:use") ? (
              <WorkspaceVolumesPage
                canManage={
                  orgHas("organization:manage") ||
                  siteHas("site:agent-workspaces:manage")
                }
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="workspace-storage"
          element={
            orgHas("organization:manage") ||
            siteHas("site:agent-workspaces:manage") ? (
              <WorkspaceStoragePage
                canManage={
                  orgHas("organization:manage") ||
                  siteHas("site:agent-workspaces:manage")
                }
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="my-providers"
          element={<Navigate to="/settings/providers" replace />}
        />
        <Route path="organization/new" element={<OrganizationCreatePage />} />
        <Route
          path="organization/edit/:tab?"
          element={
            orgHas("organization:manage") ? (
              <OrganizationEditPage
                permissions={organizationPermissions}
                user={user}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="organization"
          element={
            orgHas("organization:manage") ? (
              <OrganizationPage
                permissions={organizationPermissions}
                user={user}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="billing"
          element={
            orgHas("billing:read") ? (
              <BillingPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="organizations/new"
          element={
            siteHas("site:organizations:manage") ? (
              <SiteOrganizationCreatePage
                permissions={sitePermissions}
                user={user}
              />
            ) : (
              <Navigate to="/settings/organizations" replace />
            )
          }
        />
        <Route
          path="organizations/:organizationId/:tab?"
          element={
            siteHas("site:organizations:read") ? (
              <SiteOrganizationEditorPage
                permissions={sitePermissions}
                user={user}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="organizations"
          element={
            siteHas("site:organizations:read") ? (
              <SiteOrganizationsPage
                permissions={sitePermissions}
                user={user}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="general"
          element={
            siteHas("site:users:manage") ? (
              <GeneralSettingsPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="network-policy"
          element={
            siteHas("site:settings:manage") ? (
              <NetworkPolicyPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="providers"
          element={
            new URLSearchParams(location.search).get("scope") ===
            "organization" ? (
              user.capabilities.organizationProviders &&
              (siteOrganizationId
                ? siteHas("site:organization-access:manage")
                : orgHas("models:read")) ? (
                <ProvidersPage
                  canWrite={Boolean(
                    siteOrganizationId
                      ? siteHas("site:organization-access:manage")
                      : orgHas("organization:manage"),
                  )}
                  scope="organization"
                  organizationId={siteOrganizationId ?? undefined}
                />
              ) : (
                <Navigate to="/settings/profile" replace />
              )
            ) : siteHas("site:providers:read") ||
              orgHas("providers:personal") ||
              (user.capabilities.organizationProviders &&
                orgHas("models:read")) ? (
              <ProvidersPage
                canWrite={
                  siteHas("site:providers:write") ||
                  orgHas("providers:personal") ||
                  (user.capabilities.organizationProviders &&
                    orgHas("organization:manage"))
                }
                scope="all"
                canWriteScopes={[
                  ...(siteHas("site:providers:write")
                    ? ["platform" as const]
                    : []),
                  ...(user.capabilities.organizationProviders &&
                  orgHas("organization:manage")
                    ? ["organization" as const]
                    : []),
                  ...(orgHas("providers:personal")
                    ? ["personal" as const]
                    : []),
                ]}
                availableScopes={[
                  ...(siteHas("site:providers:write")
                    ? ["platform" as const]
                    : []),
                  ...(user.capabilities.organizationProviders &&
                  orgHas("organization:manage")
                    ? ["organization" as const]
                    : []),
                  ...(orgHas("providers:personal")
                    ? ["personal" as const]
                    : []),
                ]}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="custom-providers"
          element={
            siteHas("site:providers:read") ||
            siteHas("site:providers:write") ||
            orgHas("providers:write") ||
            orgHas("organization:manage") ||
            orgHas("providers:personal") ? (
              <Suspense
                fallback={
                  <div className="grid flex-1 place-items-center text-sm text-stone-400">
                    {t("Loading custom providers…")}
                  </div>
                }
              >
                <CustomProvidersPage
                  organizationId={siteOrganizationId ?? undefined}
                  availableScopes={[
                    ...(siteHas("site:providers:write")
                      ? (["platform"] as const)
                      : []),
                    ...((user.capabilities.organizationProviders &&
                      (orgHas("providers:write") ||
                        orgHas("organization:manage"))) ||
                    (siteOrganizationId &&
                      siteHas("site:organization-access:manage"))
                      ? (["organization"] as const)
                      : []),
                    ...(orgHas("providers:personal")
                      ? (["personal"] as const)
                      : []),
                  ]}
                />
              </Suspense>
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="models"
          element={
            orgHas("models:read") || siteHas("site:models:read") ? (
              <ModelsPage
                canWrite={siteHas("site:models:write")}
                canManageCustomModels={
                  siteHas("site:models:write") ||
                  orgHas("models:write") ||
                  orgHas("organization:manage")
                }
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="custom-models"
          element={
            siteHas("site:models:write") ||
            orgHas("models:write") ||
            orgHas("organization:manage") ? (
              <CustomModelsPage
                availableScopes={[
                  ...(siteHas("site:models:write")
                    ? (["platform"] as const)
                    : []),
                  ...(orgHas("models:write") || orgHas("organization:manage")
                    ? (["organization"] as const)
                    : []),
                  ...(orgHas("models:write") ? (["personal"] as const) : []),
                ]}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="workflow"
          element={
            orgHas("models:read") || siteHas("site:models:write") ? (
              <WorkflowSettingsPage
                canManageGlobal={siteHas("site:models:write")}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="tools"
          element={
            siteHas("site:tools:write") ? (
              <Suspense
                fallback={
                  <div className="grid flex-1 place-items-center text-sm text-stone-400">
                    {t("Loading tool editor…")}
                  </div>
                }
              >
                <ToolsPage />
              </Suspense>
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="usage"
          element={
            orgHas("usage:read") ? (
              <UsagePage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="logs/explorer"
          element={
            siteHas("site:logs:read") ? (
              <ServerLogExplorerPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="logs"
          element={
            siteHas("site:logs:read") ? (
              <ServerLogsPage />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="people"
          element={
            siteHas("site:users:manage") ? (
              <UsersPage
                canWrite={siteHas("site:users:manage")}
                currentUserId={user.id}
              />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="profile" replace />} />
      </Routes>
    </div>
  );
}
